/**
Copyright (c) Andrea Carraro

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

https://github.com/toomuchdesign/next-use-contextual-routing/blob/master/LICENSE.md
*/
import { useCallback } from 'react';

import { useRouter } from 'next/router.js';

import stringify from 'qs-stringify';

export const RETURN_HREF_QUERY_PARAM = '_UCR_return_href';

/**
 * During contextual routing browser URL will be controlled by Next Router's "as" prop
 * while the page actually rendered is defined by Next Router's "href" prop.
 *
 * During contextual navigation Next Router's behaves as follows:
 * router.asPath:   /item/3               reflects current URL and updates at each page change
 * router.pathname: /search/[terms]       stay the same as long as initial page doesn't change
 * router.query:    {"terms": "foo-bar"}  same as above
 */
export function useContextualRouting({
    queryToKeep,
}: {
    queryToKeep?: string[];
} = {}): {
    returnHref: string;
    makeContextualHref: (
        extraParams?: Record<string, string | number>,
    ) => string;
} {
    const router = useRouter();
    const returnHrefQueryParam =
        typeof router.query[RETURN_HREF_QUERY_PARAM] === 'string'
            ? router.query[RETURN_HREF_QUERY_PARAM]
            : undefined;
    const watchedQuery = Object.assign({}, router.query);
    delete watchedQuery[RETURN_HREF_QUERY_PARAM];

    /*
     * After a page refresh there is no RETURN_HREF_QUERY_PARAM in router.query
     * RETURN_HREF_QUERY_PARAM is only available in those history entries where
     * contextual navigation is enabled (or WAS enabled in case history.back() is triggered)
     */
    const returnHref = returnHrefQueryParam ?? router.asPath;
    // @NOTE JSON.stringify might be replaced with any hashing solution
    const queryHash = JSON.stringify(watchedQuery);
    const makeContextualHref = useCallback(
        (extraParams?: Record<string, string | number>) =>
            router.pathname +
            '?' +
            stringify(
                Object.assign(
                    Object.fromEntries(
                        Object.entries(router.query).filter(([key]) =>
                            (queryToKeep ?? []).includes(key),
                        ),
                    ),
                    extraParams,
                    {
                        [RETURN_HREF_QUERY_PARAM]: returnHref,
                    },
                ),
            ),
        [returnHref, queryHash, queryToKeep],
    );

    return { returnHref, makeContextualHref };
}
